import { Link, type LinkProps } from "@tanstack/react-router";
import * as React from "react";

type Props = {
  action: string;
  mask?: string;
  /**
   * Unmasked route. Defaults to `"."` (current route). Use an absolute path when the link
   * lives in a shared shell (e.g. admin sidebar) so navigation does not keep the wrong page
   * while only updating the URL mask.
   */
  to?: LinkProps["to"];
  prefill?: Record<string, unknown>;
  /** Shallow-merged into search after `action` / `prefill` (e.g. clear keys with `undefined`). */
  searchMerge?: Record<string, unknown>;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
} & Omit<LinkProps, "to" | "search" | "mask" | "className" | "disabled">;

export const MaskLink = React.forwardRef<React.ElementRef<typeof Link>, Props>(
  function MaskLink(
    {
      action,
      mask,
      to = ".",
      prefill,
      searchMerge,
      children,
      className,
      disabled,
      ...rest
    },
    ref,
  ) {
    return (
      <Link
        ref={ref}
        {...(rest as LinkProps)}
        to={to as LinkProps["to"]}
        search={(prev: Record<string, unknown>) => ({
          ...prev,
          action,
          ...(prefill !== undefined ? { prefill } : {}),
          ...(searchMerge ?? {}),
        })}
        {...(mask ? { mask: { to: mask as never } } : {})}
        {...(className ? { className } : {})}
        {...(disabled ? { disabled } : {})}
      >
        {children}
      </Link>
    );
  },
);
